import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { config } from '../core/config';
import AuthService from './auth';
import { axiosRetry } from '../utils/networkRetry';
import { getNavigateRef } from '../utils/navigationRef';
import { updateLastActivity } from '../hooks/useIdleTimeout';
import {
  LoginCredentials,
  LoginResult,
  VehicleModel,
  Operator,
  FileSigningOptions,
  CreateIncidentRequest,
  UpdateIncidentRequest,
  ConfigVersion,
  ApiError
} from '../types/api.types';

class ApiService {
  private axiosInstance: AxiosInstance;
  private loginAxiosInstance: AxiosInstance;

  private isLocalDev(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  private buildLoginBaseUrl(): string {
    return `https://login.${config.apiHost}/api`;
  }

  private buildTenantBaseUrl(tenant: string): string {
    return `https://${tenant}.${config.apiHost}/api`;
  }

  constructor() {
    const isLocalDev = this.isLocalDev();
    
    // Main API instance - will use tenant-based URLs after login
    this.axiosInstance = axios.create({
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    // Login API instance - uses proxy in local dev, or direct URL in production
    this.loginAxiosInstance = axios.create({
      baseURL: isLocalDev ? '/api' : this.buildLoginBaseUrl(),
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    this.setupInterceptors();
    
    // Add retry logic to both instances
    axiosRetry(this.axiosInstance, {
      retries: 3,
      retryDelay: 1000,
      exponentialBackoff: true,
      onRetry: (retryCount, error) => {
        console.log(`Retrying request (attempt ${retryCount}):`, error.message);
      }
    });
    
    axiosRetry(this.loginAxiosInstance, {
      retries: 2,
      retryDelay: 500
    });
  }

  private async updateBaseUrl() {
    const isLocalDev = this.isLocalDev();
    const userData = await AuthService.getUserData();

    if (isLocalDev) {
      // Use proxy for local development
      this.axiosInstance.defaults.baseURL = '/api';
    } else if (userData?.tenant) {
      this.axiosInstance.defaults.baseURL = this.buildTenantBaseUrl(userData.tenant);
    } else {
      // Use the login host until a tenant is known.
      this.axiosInstance.defaults.baseURL = this.buildLoginBaseUrl();
    }
  }

  private setupInterceptors() {
    // Request interceptor to add auth token and update base URL
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        await this.updateBaseUrl();
        updateLastActivity();
        
        const token = await AuthService.getToken();
        
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiError>) => {
        if (error.response?.status === 401) {
          await AuthService.clearAuth();
          const navigate = getNavigateRef();
          if (navigate) {
            navigate('/login');
          } else {
            window.location.href = '/login';
          }
        }
        
        const errorMessage =
          error.response?.data?.message || 
          error.response?.data?.error || 
          error.message || 
          'An unexpected error occurred';

        const apiError = new Error(errorMessage) as Error & { statusCode?: number; responseData?: unknown };
        apiError.statusCode = error.response?.status;
        apiError.responseData = error.response?.data;

        return Promise.reject(apiError);
      }
    );
  }

  // Authentication endpoints
  async login(credentials: LoginCredentials): Promise<LoginResult> {
    // Use the login-specific instance
    const response = await this.loginAxiosInstance.post<LoginResult>('/auth/login', {
      username: credentials.email,
      password: credentials.password,
      tenant: config.tenant
    });
    
    // Handle double-serialized JSON from .NET API
    let loginData = response.data;
    if (typeof loginData === 'string') {
      try {
        loginData = JSON.parse(loginData);
      } catch (error) {
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
    
    await AuthService.setAuth(loginData);
    
    // Update the base URL now that we have the tenant
    await this.updateBaseUrl();
    
    return loginData;
  }

  async exchangeSsoToken(params: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
    clientId: string;
  }): Promise<LoginResult> {
    const response = await this.loginAxiosInstance.post<LoginResult>('/auth/sso/token', {
      code: params.code,
      codeVerifier: params.codeVerifier,
      redirectUri: params.redirectUri,
      clientId: params.clientId,
    });

    let loginData = response.data;
    if (typeof loginData === 'string') {
      try {
        loginData = JSON.parse(loginData);
      } catch (error) {
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }

    await AuthService.setAuth(loginData);
    await this.updateBaseUrl();
    return loginData;
  }

  async logout(): Promise<void> {
    try {
      await this.axiosInstance.post('/auth/logout');
    } catch (error) {
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    } finally {
      await AuthService.clearAuth();
    }
  }

  async checkVersion(): Promise<ConfigVersion> {
    const response = await this.axiosInstance.get<ConfigVersion>('/config/version');
    return response.data;
  }

  // Vehicle endpoints
  async getVehicle(vinOrCode: string): Promise<VehicleModel> {
    const response = await this.axiosInstance.get<VehicleModel>(`/vehicle/${vinOrCode}`);
    
    // Handle double-serialized JSON if needed
    let data = response.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
    
    return data;
  }

  // Operator endpoints
  async getOperators(): Promise<Operator[]> {
    const response = await this.axiosInstance.get<Operator[]>('/operator/');
    return response.data;
  }

  // File upload endpoints
  async getFileSigningOptions(): Promise<FileSigningOptions> {
    const response = await this.axiosInstance.get<FileSigningOptions>('/fileupload/getsigningoptions');
    return response.data;
  }

  async uploadPhotoToAzure(url: string, blob: Blob): Promise<void> {
    await axios.put(url, blob, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': 'application/octet-stream'
      }
    });
  }

  // Incident endpoints
  async createIncident(incident: CreateIncidentRequest): Promise<number> {
    const response = await this.axiosInstance.post<number>('/Incident/Create', incident);
    return response.data;
  }

  async updateIncident(incident: UpdateIncidentRequest): Promise<number> {
    const response = await this.axiosInstance.put<number>('/Incident/update', incident);
    return response.data;
  }

  // Generic request method for flexibility
  async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.request<T>(config);
    return response.data;
  }
}

export const Api = new ApiService();
export default Api;